/**
 * routed-context.ts — WHICH ACCOUNT AM I STANDING IN? (12 August 2026)
 *
 * ⛔⛔ THE DEFECT. `RootRedirect` sent every render of `/` to
 * `/dash/${auth.primaryAccountId}` — not only at sign-in, but ANY time bare `/`
 * rendered. So anything that navigated home mid-session silently teleported the
 * person out of the account they had switched into and back to their primary.
 * Jeff's report is the exact path: switched to Cybercraft → Manage Access →
 * External Access → redeemed a consultant code → clicked "Back to my account" on
 * the confirmation → `navigate('/')` → Nexanomics Pty's dashboard. He had gone to
 * verify the grant he had just made and was shown a different company.
 *
 * ⭐⭐ THE FIX IS TO MAKE `/` MEAN "HOME IN MY CURRENT CONTEXT". That is already
 * the platform's model everywhere else — ADR-081's context model has you STANDING
 * IN a context until you deliberately switch, and `ContextSelectModal` treats a
 * switch as a full teardown. `/` was the one surface that quietly overrode it.
 * Fixing it here fixes every "go home" affordance at once — `NotFound`'s "Back to
 * home", the partner-prefill "← Back to dashboard", and the consultant-invite
 * buttons — rather than patching each one and waiting for the next.
 *
 * ⛔ SESSION storage, NOT local. The context belongs to the tab and to the
 * sign-in. `localStorage` would carry one tab's account into another and survive
 * a sign-out — the exact class of bug `session-state.ts` records for the
 * active-domain pointers ("the only per-sign-in state that SURVIVED sign-out").
 *
 * ⚠ AND IT IS RE-AUTHORIZED BEFORE USE, NEVER TRUSTED. `preferredHome` returns
 * the remembered account ONLY when it is still in the caller's own account list.
 * A stored id whose membership has since been revoked would otherwise send the
 * person to a dashboard `RouteGuard` refuses — turning "you were moved to the
 * wrong account" into "your home page is broken". Unknown → primary.
 *
 * ⚠ IT IS NOT A SECURITY BOUNDARY AND MUST NOT BE READ AS ONE. Nothing here
 * grants access to anything: every account-scoped route is authorized server-side
 * on its own. This only decides which of the caller's OWN authorized homes `/`
 * lands on. A tampered value can at worst be ignored.
 */

/** Per-tab record of the account the person is currently standing in. */
export const ROUTED_CONTEXT_KEY = 'nexa_routed_account';

/** The account id in a portal path, or undefined when the path names none. */
export function accountIdFromPath(pathname: string): string | undefined {
  const m = /^\/dash\/([^/?#]+)/.exec(pathname);
  return m?.[1] ? decodeURIComponent(m[1]) : undefined;
}

export function rememberRoutedAccount(accountId: string | undefined): void {
  if (!accountId) return;
  try {
    sessionStorage.setItem(ROUTED_CONTEXT_KEY, accountId);
  } catch {
    // private mode — the redirect simply falls back to the primary
  }
}

export function readRoutedAccount(): string | undefined {
  try {
    return sessionStorage.getItem(ROUTED_CONTEXT_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function clearRoutedAccount(): void {
  try {
    sessionStorage.removeItem(ROUTED_CONTEXT_KEY);
  } catch {
    // private mode — nothing stored, nothing to clear
  }
}

/**
 * Where `/` should land. Pure — the caller supplies the facts.
 *
 * @param remembered the account this tab was last standing in
 * @param ownAccountIds the caller's own accounts, as resolved by `/portal/me`
 * @param primaryAccountId the incumbent destination
 * @returns an account id, or undefined when the caller has no account home
 */
export function preferredHomeAccountId(
  remembered: string | undefined,
  ownAccountIds: readonly string[] | undefined,
  primaryAccountId: string | undefined,
): string | undefined {
  // ⚠ THE MEMBERSHIP TEST IS NOT DECORATION. Without it a revoked or mistyped id
  // becomes a refusal on the person's own home page.
  if (remembered && (ownAccountIds ?? []).includes(remembered)) return remembered;
  return primaryAccountId;
}
